/**
 * The unit a property type is quoted in, when nobody has said otherwise.
 *
 * The mirror of `backend/app/underwriting/measure_defaults.py`; both are held
 * to one fixture (`backend/tests/fixtures/measure_defaults_parity.json`), so
 * the form's "Units (default)" and the value the server stores are one answer.
 *
 * A default, never a statement: it applies only while `unit_of_measure` is
 * unset, the server records it as a platform default, and a unit a package
 * stated or a person chose always wins. A property type this table does not name
 * has NO default — a unit guessed for "Data Center" is a fact made up.
 */

/** The client's own spellings (Helios_Field Categories.xlsx, 14 Oct 2026): `sf` and `Acre`. */
export type Measure = 'Units' | 'Rooms' | 'Keys' | 'sf' | 'Pads' | 'Beds' | 'Acre'

/**
 * Catalogue property type -> the measure it is quoted in. Keyed on the client's Property Type
 * list; a type nobody has decided (Health Care, Warehouse, Parking, ...) has NO default.
 * Hospitality is quoted in Keys (Edward, 2 Nov 2026, UM-03: "keys is fine").
 */
export const MEASURE_BY_ASSET_TYPE: Readonly<Record<string, Measure>> = Object.freeze({
  Multifamily: 'Units',
  'Senior Housing': 'Units',
  'Student Housing': 'Beds',
  'Manufactured Housing': 'Pads',
  Hospitality: 'Keys',
  Office: 'sf',
  Industrial: 'sf',
  Retail: 'sf',
  'Medical Office': 'sf',
  'Self Storage': 'sf',
  'Mixed Use': 'sf',
  'Land and Lots': 'Acre',
})

/**
 * Every spelling of every catalogue property type, in `key()`'s comparison form — each resolves to the
 * catalogue type it names (no new type).
 *
 * This is the shared property-type vocabulary's table (`PROPERTY_TYPE_SPELLINGS` in
 * `backend/app/parsing/vocabularies.py`): the catalogue labels, the engine's enum names, the parser's
 * spellings and the record-side ones. It is not edited here — `backend/tests/test_vocabularies.py` fails if
 * this table and the server's differ by a single spelling.
 */
export const ASSET_TYPE_SPELLINGS: Readonly<Record<string, string>> = Object.freeze({
  multifamily: 'Multifamily',
  office: 'Office',
  retail: 'Retail',
  hospitality: 'Hospitality',
  industrial: 'Industrial',
  'mobile home park': 'Mobile Home Park',
  'self storage': 'Self Storage',
  'health care': 'Health Care',
  condominium: 'Condominium',
  'mixed use': 'Mixed Use',
  other: 'Other',
  'land and lots': 'Land and Lots',
  'manufactured housing': 'Manufactured Housing',
  'data center': 'Data Center',
  'cooperative housing': 'Cooperative Housing',
  'student housing': 'Student Housing',
  warehouse: 'Warehouse',
  'single family': 'Single Family',
  'no property': 'No Property',
  'medical office': 'Medical Office',
  'multifamily 1 to 4': 'Multifamily 1 TO 4',
  parking: 'Parking',
  agriculture: 'Agriculture',
  'condo conversion': 'Condo Conversion',
  'for sale units': 'For Sale Units',
  'condo inventory': 'Condo Inventory',
  'senior housing': 'Senior Housing',
  'office conversion': 'Office Conversion',
  'multi family': 'Multifamily',
  apartment: 'Multifamily',
  apartments: 'Multifamily',
  'garden apartments': 'Multifamily',
  bfr: 'Multifamily',
  'build for rent': 'Multifamily',
  multi: 'Multifamily',
  multis: 'Multifamily',
  multifam: 'Multifamily',
  mf: 'Multifamily',
  apts: 'Multifamily',
  'shopping center': 'Retail',
  'strip center': 'Retail',
  'grocery anchored': 'Retail',
  'grocery anchored retail': 'Retail',
  hotel: 'Hospitality',
  resort: 'Hospitality',
  logistics: 'Industrial',
  flex: 'Industrial',
  distribution: 'Industrial',
  storage: 'Self Storage',
  condo: 'Condominium',
  condos: 'Condominium',
  condominiums: 'Condominium',
  land: 'Land and Lots',
  'development land': 'Land and Lots',
  mhc: 'Manufactured Housing',
  'manufactured housing community': 'Manufactured Housing',
  student: 'Student Housing',
  mob: 'Medical Office',
  'parking structure': 'Parking',
  'parking garage': 'Parking',
  'automated parking': 'Parking',
  'parking facility': 'Parking',
  'senior living': 'Senior Housing',
  'assisted living': 'Senior Housing',
  'independent living': 'Senior Housing',
  'office to residential': 'Office Conversion',
})

function key(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-/]+/g, ' ')
    .trim()
}

/** The catalogue property type a value names, or null when it names none. */
export function canonicalAssetType(assetType: unknown): string | null {
  const k = key(assetType)
  return k ? (ASSET_TYPE_SPELLINGS[k] ?? null) : null
}

/** The unit this property type is measured in, or null for no default. */
export function defaultMeasureFor(assetType: unknown): Measure | null {
  const canonical = canonicalAssetType(assetType)
  return canonical ? (MEASURE_BY_ASSET_TYPE[canonical] ?? null) : null
}

/**
 * How a measure reads on screen. A hotel counts KEYS; `Rooms` was the catalogue's one word
 * for it, so a stored `Rooms` reads as Keys on a hotel — display only; nothing stored or
 * derived reads this.
 *
 * Pass `options` (the catalogue's units) wherever the label sits beside the list itself:
 * `Keys` is now a unit of its own on the client's list, and a picker that showed `Rooms` as
 * "Keys" next to a real `Keys` would offer the same word twice. Where the label is already
 * an option, the stored word is shown as it is.
 */
export function measureDisplayLabel(
  measure: unknown,
  assetType?: unknown,
  options?: readonly string[],
): string {
  const text = String(measure ?? '').trim()
  if (text.toLowerCase() === 'rooms' && canonicalAssetType(assetType) === 'Hospitality') {
    if (options?.some((o) => o.trim().toLowerCase() === 'keys')) return text
    return 'Keys'
  }
  return text
}
